import { parseLocalizedNumber } from "./formatting";

export function canonicalFromSi(
    sourceValue: number,
    sourceFactorToBase: number,
    targetFactorToBase: number,
): number | null {
    if (sourceFactorToBase <= 0 || targetFactorToBase <= 0) {
        return null;
    }

    return (sourceValue * sourceFactorToBase) / targetFactorToBase;
}

export function canonicalFromRecipe(sourceValue: number, recipeFactor: number): number | null {
    if (recipeFactor <= 0) {
        return null;
    }

    return sourceValue * recipeFactor;
}

export function formatCanonicalValue(value: number, precision?: number): string {
    if (precision !== undefined) {
        return value.toFixed(precision);
    }

    return String(Number(value.toPrecision(12)));
}

export type RangeBound = "min" | "max";

export function exceededBound(value: number, minValue?: number | null, maxValue?: number | null): RangeBound | null {
    if (minValue != null && value < minValue) {
        return "min";
    }
    if (maxValue != null && value > maxValue) {
        return "max";
    }

    return null;
}

export type QuestionUnitConversion =
    | { status: "incomplete" }
    | { status: "out_of_range"; canonicalValue: string; bound: RangeBound; limit: number }
    | { status: "ready"; canonicalValue: string };

type ConvertToQuestionUnitInput = {
    sourceValue: string;
    locale: string;
    factor: number | null;
    precision?: number;
    minValue?: number | null;
    maxValue?: number | null;
};

export function convertToQuestionUnit(input: ConvertToQuestionUnitInput): QuestionUnitConversion {
    const { sourceValue, locale, factor, precision, minValue, maxValue } = input;
    const parsed = parseLocalizedNumber(sourceValue, locale);
    if (parsed === "" || factor === null) {
        return { status: "incomplete" };
    }

    const canonicalValue = formatCanonicalValue(Number(parsed) * factor, precision);
    const numericValue = Number(canonicalValue);
    const bound = exceededBound(numericValue, minValue, maxValue);
    if (bound === "min" && minValue != null) {
        return { status: "out_of_range", canonicalValue, bound, limit: minValue };
    }
    if (bound === "max" && maxValue != null) {
        return { status: "out_of_range", canonicalValue, bound, limit: maxValue };
    }

    return { status: "ready", canonicalValue };
}
